import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useState } from "react";
import type { AdminVideosResponseDto, VideoTargetType, VideoUploadSessionResponseDto } from "@madamuh/domain";
import { Link, useParams } from "react-router-dom";
import {
  completeAdminVideoUpload,
  createAdminVideo,
  createAdminVideoUploadSession,
  fetchAdminVideos,
  hideAdminVideo,
  patchAdminVideo,
  republishAdminVideo
} from "../../shared/api/video";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/events", label: "イベント" }
];

function toDatetimeLocalValue(value: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toIsoString(value: string): string | null {
  if (!value) {
    return null;
  }

  return new Date(value).toISOString();
}

type EditState = {
  title: string;
  description: string;
  publishFrom: string;
  publishTo: string;
};

function buildEditStateMap(videos: AdminVideosResponseDto | null): Record<string, EditState> {
  if (!videos) {
    return {};
  }

  return Object.fromEntries(
    videos.items.map((video) => [
      video.videoId,
      {
        title: video.title ?? "",
        description: video.description ?? "",
        publishFrom: toDatetimeLocalValue(video.publishFrom),
        publishTo: toDatetimeLocalValue(video.publishTo)
      }
    ])
  );
}

export function AdminVideosPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { status, session, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [videos, setVideos] = useState<AdminVideosResponseDto | null>(null);
  const [videoEdits, setVideoEdits] = useState<Record<string, EditState>>({});
  const [uploadSessions, setUploadSessions] = useState<Record<string, VideoUploadSessionResponseDto>>({});
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [actionState, setActionState] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [videoPurpose, setVideoPurpose] = useState("lessonSupport");
  const [targetType, setTargetType] = useState<VideoTargetType>("lesson");
  const [targetId, setTargetId] = useState("lesson_regular_kids_hiphop");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [publishFrom, setPublishFrom] = useState("");
  const [publishTo, setPublishTo] = useState("");
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  async function refreshVideos() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const nextVideos = await fetchAdminVideos(tenantSlug);
      setVideos(nextVideos);
      setVideoEdits(buildEditStateMap(nextVideos));
      setLoadState("idle");
    } catch (error) {
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "admin videos の取得に失敗しました");
    }
  }

  useEffect(() => {
    if (status !== "authenticated" || !hasAdminRole) {
      return;
    }

    void refreshVideos();
  }, [hasAdminRole, status, tenantSlug]);

  useEffect(() => {
    if (targetType === "lesson") {
      setTargetId("lesson_regular_kids_hiphop");
      return;
    }

    if (targetType === "occurrence") {
      setTargetId("occ_demo_family_change_001");
      return;
    }

    setTargetId("event_demo_showcase_001");
  }, [targetType]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionState("create");
    setLoadError(null);

    try {
      await createAdminVideo(tenantSlug, {
        videoPurpose: videoPurpose === "replacement" ? "replacement" : videoPurpose === "eventSupport" ? "eventSupport" : "lessonSupport",
        targetType,
        title: title || null,
        description: description || null,
        publishFrom: toIsoString(publishFrom),
        publishTo: toIsoString(publishTo),
        lessonId: targetType === "lesson" ? targetId.trim() : null,
        occurrenceId: targetType === "occurrence" ? targetId.trim() : null,
        eventId: targetType === "event" ? targetId.trim() : null
      });

      setTitle("");
      setDescription("");
      setPublishFrom("");
      setPublishTo("");
      await refreshVideos();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "video create に失敗しました");
    } finally {
      setActionState(null);
    }
  }

  async function handleUploadSession(videoId: string) {
    setActionState(`upload:${videoId}`);
    setLoadError(null);

    try {
      const nextUploadSession = await createAdminVideoUploadSession(tenantSlug, videoId);
      setUploadSessions((current) => ({
        ...current,
        [videoId]: nextUploadSession
      }));
      await refreshVideos();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "upload-session の作成に失敗しました");
    } finally {
      setActionState(null);
    }
  }

  async function handleComplete(videoId: string) {
    const uploadSession = uploadSessions[videoId];

    if (!uploadSession) {
      setLoadError("先に upload-session を発行してください。");
      return;
    }

    setActionState(`complete:${videoId}`);
    setLoadError(null);

    try {
      await completeAdminVideoUpload(tenantSlug, videoId, {
        uploadSessionToken: uploadSession.uploadSessionToken,
        originalFileName: `${videoId}.mp4`,
        mimeType: "video/mp4",
        fileSizeBytes: 20_000_000,
        durationSeconds: 180
      });

      setUploadSessions((current) => {
        const next = { ...current };
        delete next[videoId];
        return next;
      });
      await refreshVideos();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "upload complete に失敗しました");
    } finally {
      setActionState(null);
    }
  }

  async function handlePatch(videoId: string) {
    const editState = videoEdits[videoId];

    if (!editState) {
      return;
    }

    setActionState(`patch:${videoId}`);
    setLoadError(null);

    try {
      await patchAdminVideo(tenantSlug, videoId, {
        title: editState.title || null,
        description: editState.description || null,
        publishFrom: toIsoString(editState.publishFrom),
        publishTo: toIsoString(editState.publishTo)
      });
      await refreshVideos();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "video patch に失敗しました");
    } finally {
      setActionState(null);
    }
  }

  async function handleHide(videoId: string) {
    setActionState(`hide:${videoId}`);
    setLoadError(null);

    try {
      await hideAdminVideo(tenantSlug, videoId);
      await refreshVideos();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "video hide に失敗しました");
    } finally {
      setActionState(null);
    }
  }

  async function handleRepublish(videoId: string) {
    setActionState(`republish:${videoId}`);
    setLoadError(null);

    try {
      await republishAdminVideo(tenantSlug, videoId);
      await refreshVideos();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "video republish に失敗しました");
    } finally {
      setActionState(null);
    }
  }

  return (
    <PageScaffold
      title="Admin Videos"
      description="create -> upload-session -> complete -> auto-publish を確認する最小画面です。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            admin 画面には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock operator login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}
        {status === "authenticated" && !hasAdminRole ? <p>この画面は admin role のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>admin videos を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        <form onSubmit={handleCreate} style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Create Draft</h2>
          <label style={{ display: "grid", gap: 4 }}>
            <span>videoPurpose</span>
            <select value={videoPurpose} onChange={(event) => setVideoPurpose(event.target.value)}>
              <option value="lessonSupport">lessonSupport</option>
              <option value="replacement">replacement</option>
              <option value="eventSupport">eventSupport</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>targetType</span>
            <select value={targetType} onChange={(event: ChangeEvent<HTMLSelectElement>) => setTargetType(event.target.value as VideoTargetType)}>
              <option value="lesson">lesson</option>
              <option value="occurrence">occurrence</option>
              <option value="event">event</option>
            </select>
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>targetId</span>
            <input value={targetId} onChange={(event) => setTargetId(event.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>title</span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>description</span>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>publishFrom</span>
            <input type="datetime-local" value={publishFrom} onChange={(event) => setPublishFrom(event.target.value)} />
          </label>
          <label style={{ display: "grid", gap: 4 }}>
            <span>publishTo</span>
            <input type="datetime-local" value={publishTo} onChange={(event) => setPublishTo(event.target.value)} />
          </label>
          <button type="submit" disabled={actionState === "create"}>
            {actionState === "create" ? "作成中..." : "Create Video Draft"}
          </button>
          <div style={{ fontSize: 13, opacity: 0.8 }}>
            demo target ids: `lesson_regular_kids_hiphop`, `occ_demo_family_change_001`, `occ_demo_ben_cancelled_001`, `event_demo_showcase_001`
          </div>
        </form>

        <section style={{ display: "grid", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Videos</h2>
          {videos?.items.length === 0 ? <p style={{ margin: 0 }}>動画はありません。</p> : null}

          {videos?.items.map((video) => {
            const uploadSession = uploadSessions[video.videoId];

            return (
              <article
                key={video.videoId}
                style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 10 }}
              >
                <div style={{ display: "grid", gap: 4 }}>
                  <strong>{video.title ?? "タイトル未設定"}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    {video.videoPurpose} / {video.targetType} / {video.targetLabel}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    uploadStatus: {video.uploadStatus} / publishStatus: {video.publishStatus}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    publishFrom: {video.publishFrom ?? "未設定"} / publishTo: {video.publishTo ?? "未設定"}
                  </div>
                  {video.publishErrorCodes.length > 0 ? (
                    <div style={{ fontSize: 14 }}>publishErrorCodes: {video.publishErrorCodes.join(", ")}</div>
                  ) : null}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => void handleUploadSession(video.videoId)}
                    disabled={actionState === `upload:${video.videoId}`}
                  >
                    {actionState === `upload:${video.videoId}` ? "発行中..." : "Upload Session"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleComplete(video.videoId)}
                    disabled={actionState === `complete:${video.videoId}`}
                  >
                    {actionState === `complete:${video.videoId}` ? "完了中..." : "Complete Upload"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleHide(video.videoId)}
                    disabled={actionState === `hide:${video.videoId}`}
                  >
                    {actionState === `hide:${video.videoId}` ? "処理中..." : "Hide"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRepublish(video.videoId)}
                    disabled={actionState === `republish:${video.videoId}`}
                  >
                    {actionState === `republish:${video.videoId}` ? "処理中..." : "Republish"}
                  </button>
                </div>

                {uploadSession ? (
                  <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
                    <strong>Fake Upload Session</strong>
                    <span>{uploadSession.uploadUrl}</span>
                    <span>token: {uploadSession.uploadSessionToken}</span>
                    <span>expiresAt: {uploadSession.expiresAt}</span>
                  </div>
                ) : null}

                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>title</span>
                    <input
                      value={videoEdits[video.videoId]?.title ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            title: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>description</span>
                    <textarea
                      value={videoEdits[video.videoId]?.description ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            description: event.target.value
                          }
                        }))
                      }
                      rows={3}
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>publishFrom</span>
                    <input
                      type="datetime-local"
                      value={videoEdits[video.videoId]?.publishFrom ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            publishFrom: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>publishTo</span>
                    <input
                      type="datetime-local"
                      value={videoEdits[video.videoId]?.publishTo ?? ""}
                      onChange={(event) =>
                        setVideoEdits((current) => ({
                          ...current,
                          [video.videoId]: {
                            ...(current[video.videoId] ?? { title: "", description: "", publishFrom: "", publishTo: "" }),
                            publishTo: event.target.value
                          }
                        }))
                      }
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => void handlePatch(video.videoId)}
                    disabled={actionState === `patch:${video.videoId}`}
                  >
                    {actionState === `patch:${video.videoId}` ? "保存中..." : "Patch Metadata"}
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </PageScaffold>
  );
}
